/**
 * TaskContextPane — the facts of the task, always to hand.
 *
 * Status, priority, the due date and its urgency, when it was made and by
 * whom read off the Details tab before, which meant they left the screen the
 * moment someone opened the Feed or Attachments — exactly when "is this still
 * open, and when is it due" gets asked. As a pane they stay put.
 *
 * IT STRETCHES. The pane resizes from 260 to 720, so the two-word answers are
 * an `auto-fit` grid rather than a fixed column count: one column in a narrow
 * dock, two when it is pulled wide.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  PiCalendarBlankLight,
  PiCalendarCheckLight,
  PiCircleDashedLight,
  PiClockClockwiseLight,
  PiClockCountdownLight,
  PiFlagLight,
  PiSuitcaseSimpleLight,
  PiUserLight,
  PiUsersLight,
} from 'react-icons/pi';
import { useHistory } from 'react-router-dom';
import Badge from '../../shared/TailwindComponents/Badge';
import { Field, Well } from '../../shared/brand/Field';
import { formatDate, formatDateTime } from '../../../utils/functions';
import {
  taskDueInfo,
  taskDueTone,
  taskPriorityColor,
  taskStatusColor,
  taskStatusLabel,
} from '../taskFacts';
import { ContextSection } from '../../shared/brand/ContextPane';
import CollaboratorStack from '../../shared/access/CollaboratorStack';

interface Props {
  taskDetails: any;
  taskStatus: string;
  collaborators: any[];
  /**
   * Raises the ACCESS PANE — not the manage side panel.
   *
   * This was an "N members" button. A count is not an answer to "who is on
   * this", and the page already had three other ways to the same list, so the
   * faces are here and the reading happens in the pane.
   */
  onOpenAccess: () => void;
}

const TaskContextPane: React.FC<Props> = ({
  taskDetails,
  taskStatus,
  collaborators,
  onOpenAccess,
}) => {
  const { t } = useTranslation();
  const history = useHistory();

  if (!taskDetails) return null;

  const dueInfo = taskDueInfo(taskDetails.due_date, taskStatus, t);
  const tone = taskDueTone(dueInfo);
  const matter = taskDetails.matter;

  const creator = (taskDetails.collaborators || []).find((c: any) => c.role === 'creator');

  return (
    <div className="flex flex-col h-full min-h-0 overflow-y-auto custom-scrollbar-main px-3 py-3 space-y-4">
      {/* Overview — the two-word answers, side by side. */}
      <div>
        <ContextSection>{t('Overview')}</ContextSection>
        <div
          className="grid gap-3"
          style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))' }}
        >
          <Field icon={<PiCircleDashedLight />} label={t('Status')}>
            <Badge color={taskStatusColor(taskStatus) as any}>
              {taskStatusLabel(taskStatus, t)}
            </Badge>
          </Field>
          <Field icon={<PiFlagLight />} label={t('Priority')}>
            <Badge color={taskPriorityColor(taskDetails.priority) as any}>
              {taskDetails.priority
                ? taskDetails.priority.charAt(0).toUpperCase() + taskDetails.priority.slice(1)
                : t('Medium')}
            </Badge>
          </Field>
        </div>
      </div>

      {/* Timeline — the due date carries the urgency colour, nothing else. */}
      <div>
        <ContextSection>{t('Timeline')}</ContextSection>
        <Well className="space-y-3">
          <Field
            icon={<PiClockCountdownLight />}
            label={t('Due Date')}
            tone={tone}
            value={taskDetails.due_date ? formatDateTime(taskDetails.due_date) : undefined}
          />
          {dueInfo && (
            <p
              className={
                dueInfo.urgency === 'overdue'
                  ? 'text-[11px] font-medium text-danger-500 dark:text-danger-400 -mt-2'
                  : 'text-[11px] font-medium text-warning-600 dark:text-warning-400 -mt-2'
              }
            >
              {dueInfo.text}
            </p>
          )}
          <Field
            icon={<PiCalendarBlankLight />}
            label={t('Created')}
            value={taskDetails.created_at ? formatDateTime(taskDetails.created_at) : undefined}
            emptyText="-"
          />
          {taskDetails.updated_at && (
            <Field
              icon={<PiClockClockwiseLight />}
              label={t('Last Updated')}
              value={formatDateTime(taskDetails.updated_at)}
            />
          )}
          {taskDetails.completed_at && (
            <Field
              icon={<PiCalendarCheckLight />}
              label={t('Completed')}
              tone="success"
              value={formatDateTime(taskDetails.completed_at)}
            />
          )}
        </Well>
      </div>

      {/* People — who made it, and the way into everyone else. */}
      <div>
        <ContextSection>{t('People')}</ContextSection>
        <Well className="space-y-3">
          <Field
            icon={<PiUserLight />}
            label={t('Created By')}
            value={creator?.name || undefined}
            emptyText="-"
          />
          {/*
            SAME ROW HEIGHT AS "Created By".

            Created By renders the canonical `text-sm` line (a 20px line box);
            Access rendered a default-size avatar stack (24px discs) with no
            line box at all, so the two rows sat at different heights and
            different optical weights inside one Well. The stack is `xs` (20px)
            and lives in a 20px flex box, so both rows are one list.
          */}
          <Field icon={<PiUsersLight />} label={t('Access')}>
            <div className="flex items-center h-5">
              {collaborators.length > 0 ? (
                <CollaboratorStack
                  collaborators={collaborators}
                  size="xs"
                  onClick={onOpenAccess}
                  testId="task-context-access-stack"
                />
              ) : (
                <span className="text-sm text-fg-muted">{t('No one has access yet')}</span>
              )}
            </div>
          </Field>
        </Well>
      </div>

      {/* The matter this belongs to — one line and a way there. */}
      {matter?.uuid && (
        <div>
          <ContextSection>{t('Linked Matter')}</ContextSection>
          <Well>
            <button
              type="button"
              onClick={() => history.push(`/matters/${matter.uuid}`)}
              className="flex items-start gap-2 text-left w-full group"
            >
              <PiSuitcaseSimpleLight className="h-4 w-4 text-fg-muted flex-shrink-0 mt-0.5" />
              <span className="text-sm text-fg-body group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors line-clamp-2">
                {matter.title || taskDetails.matter_name || t('Untitled Matter')}
              </span>
            </button>
          </Well>
        </div>
      )}
    </div>
  );
};

export default TaskContextPane;
